import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import PmoConfigurationApi from '../../../middleware/api/ProjectManagement/PmoConfiguration/PmoConfigurationApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import {
  getAllPmoDetails,
  getAllPmoProps,
  PmoConfigurationSliceState,
} from '../../../types/ProjectManagement/PmoConfiguration/PmoConfigurationTypes'
import { RootState } from '../../../stateStore'

const getAllPMO = createAsyncThunk(
  'PmoConfiguration/getAllPMO',
  async (data: getAllPmoProps, thunkApi) => {
    try {
      return await PmoConfigurationApi.getAllPMO(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addPmoConfig = createAsyncThunk(
  'PmoConfiguration/addPmoConfig',
  async (empId: number, thunkApi) => {
    try {
      return await PmoConfigurationApi.addPmoConfig(empId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deletePmoConfig = createAsyncThunk(
  'PmoConfiguration/deletePmoConfig',
  async (pmoId: number, thunkApi) => {
    try {
      return await PmoConfigurationApi.deletePmoConfig(pmoId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const isCheckPMO = createAsyncThunk(
  'PmoConfiguration/isCheckPMO',
  async (empId: number, thunkApi) => {
    try {
      return await PmoConfigurationApi.isCheckPMO(empId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialPmoConfigurationSliceState: PmoConfigurationSliceState = {
  isLoading: ApiLoadingState.idle,
  getAllPmoDetails: {} as getAllPmoDetails,
  getPmoDetails: [],
  listSize: 0,
  isPMO: false,
}

const pmoConfigurationSlice = createSlice({
  name: 'PmoConfiguration',
  initialState: initialPmoConfigurationSliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllPMO.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllPmoDetails = action.payload
        state.listSize = action.payload.size
      })
      .addCase(isCheckPMO.fulfilled, (state, action) => {
        state.isPMO = action.payload
      })
      .addMatcher(
        isAnyOf(
          isCheckPMO.pending,
          deletePmoConfig.pending,
          addPmoConfig.pending,
          getAllPMO.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(deletePmoConfig.fulfilled, addPmoConfig.fulfilled),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          isCheckPMO.rejected,
          deletePmoConfig.rejected,
          addPmoConfig.rejected,
          getAllPMO.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.pmoConfiguration.isLoading

const getAllPmoData = (state: RootState): getAllPmoDetails =>
  state.pmoConfiguration.getAllPmoDetails

const listSize = (state: RootState): number => state.pmoConfiguration.listSize

const isPMO = (state: RootState): boolean => state.pmoConfiguration.isPMO

const pmoConfigurationSelectors = {
  isLoading,
  getAllPmoData,
  listSize,
  isPMO,
}

const pmoConfigurationThunk = {
  getAllPMO,
  addPmoConfig,
  deletePmoConfig,
  isCheckPMO,
}

export const pmoConfigurationService = {
  ...pmoConfigurationThunk,
  actions: pmoConfigurationSlice.actions,
  selectors: pmoConfigurationSelectors,
}

export default pmoConfigurationSlice.reducer
